import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Upload, Sparkles, Loader2, ImageIcon, X } from "lucide-react";
import { api } from "@/lib/api";
import { useProStatus } from "@/hooks/useProStatus";
import { ReceiptFields, emptyReceipt } from "@/components/ReceiptFields";
import { Button } from "@/components/ui/button";

function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function AddReceipt() {
  const navigate = useNavigate();
  const { data: pro, refresh: refreshPro } = useProStatus();
  const fileRef = useRef(null);
  const [form, setForm] = useState(emptyReceipt());
  const [image, setImage] = useState("");
  const [scanning, setScanning] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleFile = async (file) => {
    if (!file || !file.type.startsWith("image/")) return toast.error("Please choose an image file.");
    const dataUrl = await fileToDataUrl(file);
    setImage(dataUrl);
  };

  const scan = async () => {
    if (!image) return toast.error("Upload a receipt image first.");
    setScanning(true);
    try {
      const r = await api("/receipts/ocr", { method: "POST", body: { image_base64: image } });
      setForm((f) => ({
        ...f,
        vendor: r.vendor || f.vendor,
        amount: r.amount != null ? String(r.amount) : f.amount,
        receipt_date: r.receipt_date || f.receipt_date,
        category: r.category || f.category,
      }));
      toast.success(`Scanned · ${Math.round((r.confidence || 0) * 100)}% confidence. Review & save.`);
    } catch (e) {
      toast.error(e?.message || "OCR failed. Enter the details manually.");
    } finally {
      setScanning(false);
    }
  };

  const save = async () => {
    if (!form.vendor.trim()) return toast.error("Vendor is required.");
    const amount = parseFloat(form.amount);
    if (!amount || amount <= 0) return toast.error("Enter a valid amount.");
    setSaving(true);
    try {
      await api("/receipts", {
        method: "POST",
        body: {
          vendor: form.vendor.trim(),
          amount,
          receipt_date: form.receipt_date,
          category: form.category,
          notes: form.notes || "",
          image_base64: image || "",
          gps_label: form.gps_label || "",
          gallons: form.category === "Fuel" && form.gallons ? parseFloat(form.gallons) : null,
          state: form.category === "Fuel" ? (form.state || "") : "",
        },
      });
      toast.success("Receipt saved to your audit archive.");
      refreshPro();
      navigate("/receipts");
    } catch (e) {
      if (e?.status === 402) {
        toast.error("Free limit reached — upgrade to Pro for unlimited receipts.");
        navigate("/upgrade");
        return;
      }
      toast.error(e?.message || "Couldn't save receipt.");
    } finally {
      setSaving(false);
    }
  };

  const remaining = pro && !pro.is_pro ? pro.remaining : null;

  return (
    <div className="space-y-6">
      {remaining != null && (
        <div className="rounded-xl border border-rig-warning/40 bg-rig-warning/10 px-4 py-3 text-sm text-rig-warning">
          Free plan: <b>{remaining}</b> of {pro.free_limit} receipts left this month.
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Upload panel */}
        <div>
          <h3 className="mb-3 font-display text-sm font-bold uppercase tracking-[0.18em] text-rig-dim">
            Receipt Image
          </h3>
          <div
            onDragOver={(e) => {
              e.preventDefault();
              setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragOver(false);
              handleFile(e.dataTransfer.files?.[0]);
            }}
            className={`relative flex min-h-[320px] flex-col items-center justify-center rounded-2xl border-2 border-dashed p-6 text-center transition-colors ${
              dragOver ? "border-rig-accent bg-rig-accent/5" : "border-rig-borderStrong bg-rig-surface"
            }`}
          >
            {image ? (
              <>
                <img src={image} alt="receipt" className="max-h-[360px] w-auto rounded-xl object-contain" />
                <button
                  onClick={() => setImage("")}
                  data-testid="clear-image-btn"
                  className="absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-full bg-rig-bg/80 text-white hover:bg-rig-error"
                >
                  <X className="h-4 w-4" />
                </button>
              </>
            ) : (
              <>
                <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-rig-surface2">
                  <ImageIcon className="h-8 w-8 text-rig-mute" />
                </div>
                <p className="mt-4 font-semibold text-white">Drag & drop a receipt</p>
                <p className="mt-1 text-sm text-rig-dim">or use the button below · JPG / PNG / HEIC</p>
              </>
            )}
          </div>

          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            capture="environment"
            className="hidden"
            data-testid="file-input"
            onChange={(e) => handleFile(e.target.files?.[0])}
          />
          <div className="mt-3 grid grid-cols-2 gap-3">
            <Button
              variant="outline"
              onClick={() => fileRef.current?.click()}
              data-testid="upload-btn"
              className="h-11 border-rig-borderStrong bg-rig-surface text-white hover:bg-rig-surface2"
            >
              <Upload className="mr-2 h-4 w-4" /> {image ? "Replace" : "Upload"}
            </Button>
            <Button
              onClick={scan}
              disabled={!image || scanning}
              data-testid="scan-ai-btn"
              className="h-11 bg-rig-blue font-display text-xs font-bold uppercase tracking-widest text-white hover:bg-rig-blue/90"
            >
              {scanning ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4" />}
              Scan with AI
            </Button>
          </div>
        </div>

        {/* Form panel */}
        <div>
          <h3 className="mb-3 font-display text-sm font-bold uppercase tracking-[0.18em] text-rig-dim">
            Details
          </h3>
          <div className="rounded-2xl border border-rig-border bg-rig-surface p-5">
            <ReceiptFields form={form} set={set} />
            <div className="mt-6 flex gap-3">
              <Button
                variant="outline"
                onClick={() => navigate(-1)}
                className="h-12 flex-1 border-rig-borderStrong bg-transparent text-white hover:bg-rig-surface2"
              >
                Cancel
              </Button>
              <Button
                onClick={save}
                disabled={saving}
                data-testid="save-receipt-btn"
                className="h-12 flex-[2] bg-rig-accent font-display text-sm font-bold uppercase tracking-widest text-rig-accentText hover:bg-rig-accentDark"
              >
                {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : "Save Receipt"}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
